import { FC } from 'react'

import { formatEuros, SiteAds } from '@api/generated-site'

// Blocs de la page de vente du site (page publique /votre-site/<token> et panneau avant le paiement).
// Principes retenus (études 2025-2026 : Unbounce, étude A/B 2 000 pages, retours X/Reddit) : un seul
// objectif, une promesse de résultat en langage simple, des preuves précises et nommées plutôt que
// vagues, le prix et le « sans risque » collés au bouton, les objections traitées avant le dernier
// bouton, pas d'adjectifs creux. Aucun chiffre inventé : les avis sont ceux de Trustpilot, le nombre
// de sites vient de la base.

// avis Trustpilot de ma-domiciliation.fr, relevés le 03/11/2026 (à rafraîchir de temps en temps) : les plus
// enthousiastes, sur le service et sur la plateforme ; texte exact, prénom et date tels qu'affichés
export const TRUST = {
  score: '4,5',
  reviews: 1219,
  fiveStars: '85 %',
  // nombre d'entreprises domiciliées, donné par Jules (03/11/2026)
  customers: '50 000',
  url: 'https://fr.trustpilot.com/review/ma-domiciliation.fr',
  quotes: [
    { author: 'Ben', date: '7 octobre 2026', text: 'Une expérience exceptionnelle ! Je recommande à 100 %.' },
    { author: 'Michaël', date: '4 août 2026', text: 'Parfait, bon suivi administratif, site intuitif et service client très réactif et précis dans les réponses.' },
    { author: 'Jacques', date: '10 août 2026', text: 'Excellent service. Bonne réactivité et écoute du client. Ce service marche parfaitement. Bravo.' },
    { author: 'Othmane', date: '4 août 2026', text: 'Juste wow la réactivité, le service client, merci.' },
    { author: 'Stéphane C.', date: '21 juillet 2026', text: 'Ma domiciliation offre une expérience facile, le panel d’utilisateur est ergonomique.' },
    { author: 'Barbara', date: '21 juillet 2026', text: 'Procédures simplifiées. Je recommande absolument.' },
  ],
}

// prix d'un site vitrine chez un prestataire : fourchette basse du marché français des TPE
export const AGENCY_PRICE = '1 500 à 3 000 €'

// espaces insécables dans les nombres et avant « € » : la valeur ne se coupe qu'entre deux montants
const nonBreakingAmount = (amount: string) => amount.replace(/(\d) (?=\d)/g, '$1\u00A0').replace(/ €/g, '\u00A0€')

type Props = {
  brand: string
  // prix du plan mensuel de la grille du site (centimes HT), jours d'essai gratuit (0 = aucun)
  monthlyAmountHt: number | null
  trialDays: number
  variant?: 'dark' | 'light'
  // bouton « Je veux une publicité pour mon activité » (espace client connecté seulement)
  onAdInterest?: () => void
  adInterestRecorded?: boolean
}

const BENEFITS = [
  {
    title: 'Vos clients vous appellent en un geste',
    text: 'Bouton d’appel, formulaire de contact, vos prestations en clair. Le visiteur comprend en trois secondes ce que vous faites et comment vous joindre.',
    proof: 'C’est ce qui transforme une visite en appel.',
  },
  {
    title: 'On vous trouve sur Google',
    text: 'Votre nom, vos services, votre zone. Celui qui vous cherche tombe sur vous, pas sur un annuaire ou sur un concurrent.',
    proof: 'Celui qui ne vous trouve pas appelle quelqu’un d’autre.',
  },
  {
    title: 'Rien à faire, jamais',
    text: 'Hébergement, nom de domaine, sécurité, mises à jour : inclus. Une modification ? Vous la dictez à la voix, le site change.',
    proof: 'La première modification est offerte.',
  },
  {
    title: 'L’image d’une entreprise sérieuse',
    text: 'Un site à vos couleurs, avec vos mentions légales. Le même effet qu’une belle devanture.',
    proof: `Ailleurs, ce site coûte ${AGENCY_PRICE} et trois semaines.`,
  },
  {
    title: 'Et bientôt, votre publicité',
    text: 'Vous voulez plus de clients ? Nous préparons des publicités prêtes à diffuser pour votre activité, à partir de votre site.',
    proof: 'Dites-nous si ça vous intéresse.',
  },
]

export const FAQ = [
  {
    q: 'Que se passe-t-il après les 7 jours ?',
    a: 'Si vous ne faites rien, le site reste en ligne et l’abonnement démarre, réglé avec le moyen de paiement de votre compte. Si vous l’arrêtez avant, vous ne payez rien du tout.',
  },
  {
    q: 'Je peux arrêter quand ?',
    a: 'Quand vous voulez, en un clic depuis votre espace client. Sans frais, sans justification, sans appel à passer.',
  },
  {
    q: 'Je dois installer ou configurer quelque chose ?',
    a: 'Non. Le site est déjà en ligne. Vous cliquez sur « Je garde mon site », vous vous connectez, c’est fait.',
  },
  {
    q: 'Et si je veux changer un texte, une photo, une couleur ?',
    a: 'Vous le dites, à la voix ou par écrit, depuis votre espace client. Le site est modifié pour vous. La première modification est offerte.',
  },
  {
    q: 'Je peux avoir mon propre nom de domaine ?',
    a: 'Oui, il est inclus : vous le choisissez depuis votre espace client, nous nous occupons du reste.',
  },
  {
    q: 'Pourquoi si peu cher, alors qu’un site coûte des milliers d’euros ?',
    a: 'Parce que nous l’avons déjà fabriqué pour vous, à partir de ce que vous nous avez dit de votre activité. Vous ne payez que l’hébergement et le service, pas la création.',
  },
]

export const STEPS = [
  { title: 'Cliquez sur « Je garde mon site »', text: 'Un seul bouton, rien à remplir.' },
  { title: 'Connectez-vous à votre espace', text: 'Le même compte que pour votre courrier. Dix secondes.' },
  { title: 'C’est en ligne', text: 'Votre site reste à vous. Vous recevez le lien à partager. 0 € aujourd’hui.' },
]

export const monthlyLabel = (monthlyAmountHt: number | null) => (monthlyAmountHt !== null
  ? `${formatEuros(monthlyAmountHt)} HT/mois`
  : 'quelques euros par mois')

// date de fin de l'essai si l'on commence aujourd'hui (« rien à payer avant le 10 novembre »)
export const trialEndDate = (trialDays: number, now = new Date()) => new Date(now.getTime() + trialDays * 24 * 3600 * 1000)
  .toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })

// comparaison avec un prestataire : l'argument qui manquait aux clients qui aimaient le site
// sans se projeter sur l'achat
const priceComparison = (monthlyAmountHt: number | null, trialDays: number) => (trialDays > 0
  ? `déjà en ligne, ${trialDays} jours d’essai gratuit, puis ${monthlyLabel(monthlyAmountHt)} sans engagement`
  : `déjà en ligne, ${monthlyLabel(monthlyAmountHt)} sans engagement`)

// tuiles de preuve : avis Trustpilot, sites en ligne, prix ailleurs
export const ProofTiles: FC<{ sitesOnline?: number, variant?: 'dark' | 'light' }> = ({ sitesOnline, variant = 'dark' }) => {
  const dark = variant === 'dark'
  // mobile : une ligne par preuve (chiffre à gauche, légende à droite) ; bureau : trois tuiles
  const tile = `rounded-2xl px-4 py-3 md:py-4 flex items-baseline gap-3 md:block ${dark ? 'bg-white/5 border border-white/15' : 'bg-black/[0.04]'}`
  const big = `font-aeonik text-[22px] lg:text-[28px] font-medium leading-none tabular-nums whitespace-nowrap ${dark ? 'text-white' : 'text-black'}`
  // valeur longue : un cran plus petit ; en tuile, ne se coupe qu'autour du « à »
  const bigPrice = `font-aeonik text-xl lg:text-[26px] font-medium leading-tight tabular-nums whitespace-nowrap md:whitespace-normal ${dark ? 'text-white' : 'text-black'}`
  const small = `md:mt-1.5 text-xs ${dark ? 'text-white/60' : 'text-black/60'}`
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-3">
      <div className={tile}>
        <div className={big}>{`${TRUST.customers}+`}</div>
        <div className={small}>entreprises nous font confiance pour leur domiciliation</div>
      </div>
      <a href={TRUST.url} target="_blank" rel="noopener noreferrer" className={`${tile} hover:opacity-90`}>
        <div className={big}>{`${TRUST.score}/5`}</div>
        <div className={small}>{`sur Trustpilot, ${TRUST.reviews.toLocaleString('fr-FR')} avis`}</div>
      </a>
      {sitesOnline !== undefined && sitesOnline > 0 && (
        <div className={tile}>
          <div className={big}>{sitesOnline.toLocaleString('fr-FR')}</div>
          <div className={small}>sites déjà en ligne, créés comme le vôtre</div>
        </div>
      )}
      <div className={tile}>
        <div className={bigPrice}>{nonBreakingAmount(AGENCY_PRICE)}</div>
        <div className={small}>le prix du même site chez un prestataire</div>
      </div>
    </div>
  )
}

// questions-réponses : les objections traitées avant le dernier bouton
export const SiteFaq: FC<{ variant?: 'dark' | 'light', items?: typeof FAQ }> = ({ variant = 'dark', items = FAQ }) => {
  const dark = variant === 'dark'
  return (
    <div className={`divide-y ${dark ? 'divide-white/10' : 'divide-black/10'}`}>
      {items.map(item => (
        <details key={item.q} className="group py-3">
          <summary className={`cursor-pointer list-none flex items-start justify-between gap-4 font-aeonik text-base font-medium ${dark ? 'text-white' : 'text-black'}`}>
            <span>{item.q}</span>
            <span className={`shrink-0 transition-transform group-open:rotate-45 ${dark ? 'text-white/50' : 'text-black/40'}`}>+</span>
          </summary>
          <p className={`mt-2 text-sm ${dark ? 'text-white/70' : 'text-black/65'}`}>{item.a}</p>
        </details>
      ))}
    </div>
  )
}

// « micro landing page » des avantages du site : avant le paiement (panneau) et sur la page
// publique partageable
const SiteBenefits: FC<Props> = ({
  brand, monthlyAmountHt, trialDays, variant = 'light', onAdInterest, adInterestRecorded = false,
}) => {
  const dark = variant === 'dark'
  const muted = dark ? 'text-white/60' : 'text-black/60'
  const strong = dark ? 'text-white' : 'text-black'

  return (
    <div>
      <div className={`text-[11px] uppercase tracking-[0.2em] mb-2 ${dark ? 'text-white/50' : 'text-black/50'}`}>Pourquoi garder ce site</div>
      <h2 className={`font-aeonik [&&&]:text-2xl md:[&&&]:text-[30px] font-medium leading-tight [&&&]:mb-2 ${strong}`}>
        {`Ce que ce site fait pour ${brand}.`}
      </h2>
      <p className={`text-base mb-6 ${muted}`}>Pas une vitrine. Un commercial qui travaille pour vous 24 h/24, sans salaire.</p>

      <ul className="space-y-5">
        {BENEFITS.map((benefit, index) => (
          <li key={benefit.title} className="flex gap-4">
            <div className={`shrink-0 h-8 w-8 rounded-full border flex items-center justify-center font-aeonik text-sm ${dark ? 'border-white/20 text-white/80' : 'border-black/15 text-black/70'}`}>{index + 1}</div>
            <div>
              <div className={`font-aeonik text-lg md:text-xl font-medium leading-tight ${strong}`}>{benefit.title}</div>
              <div className={`text-sm mt-1 ${muted}`}>{benefit.text}</div>
              <div className={`text-sm mt-1 font-medium ${strong}`}>{benefit.proof}</div>
              {index === BENEFITS.length - 1 && onAdInterest && (
                <button
                  type="button"
                  onClick={onAdInterest}
                  disabled={adInterestRecorded}
                  className={`mt-2 text-sm underline underline-offset-4 ${dark ? 'text-white/80 hover:text-white' : 'text-black/70 hover:text-black'} disabled:no-underline disabled:opacity-80`}
                >
                  {adInterestRecorded ? 'C’est noté, nous revenons vers vous avec une proposition de publicité.' : 'Je veux une publicité pour mon activité'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div className={`mt-6 grid md:grid-cols-2 gap-3 rounded-2xl p-4 ${dark ? 'bg-white/5 border border-white/15' : 'bg-black/[0.04]'}`}>
        <div>
          <div className={`text-[11px] uppercase tracking-[0.18em] mb-1 ${muted}`}>Ailleurs</div>
          <div className={`text-sm ${strong}`}>{`Un site vitrine chez un prestataire : ${AGENCY_PRICE}, trois semaines, et un webmaster à relancer à chaque changement.`}</div>
        </div>
        <div>
          <div className={`text-[11px] uppercase tracking-[0.18em] mb-1 ${muted}`}>Ici</div>
          <div className={`text-sm font-medium ${strong}`}>
            {`Le site de ${brand} est ${priceComparison(monthlyAmountHt, trialDays)}.`}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SiteBenefits

type AdsProps = {
  ads: SiteAds
  brand: string
  variant?: 'dark' | 'light'
  // bouton « Je veux lancer cette publicité » : action (espace client) ou lien (page publique → espace client)
  onLaunch?: () => void
  launchHref?: string
  launchRecorded?: boolean
}

// publicités prêtes à diffuser, montrées sous le site : les deux visuels, ce qu'on fait, ce que le client paie
export const SiteAdsPreview: FC<AdsProps> = ({
  ads, brand, variant = 'dark', onLaunch, launchHref, launchRecorded = false,
}) => {
  const dark = variant === 'dark'
  const muted = dark ? 'text-white/60' : 'text-black/60'
  const strong = dark ? 'text-white' : 'text-black'
  const buttonClass = `btn-shine inline-block rounded-2xl px-5 py-3.5 font-aeonik text-base font-medium ${dark ? 'bg-white text-black hover:bg-white/90' : 'bg-black text-white hover:bg-black/90'}`
  const label = launchRecorded ? 'C’est noté : nous revenons vers vous pour lancer votre publicité.' : 'Je veux lancer cette publicité'
  return (
    <section>
      <div className={`text-[11px] uppercase tracking-[0.2em] mb-2 ${dark ? 'text-white/50' : 'text-black/50'}`}>Votre publicité est prête aussi</div>
      <h2 className={`font-aeonik [&&&]:text-2xl md:[&&&]:text-[30px] font-medium leading-tight [&&&]:mb-2 ${strong}`}>
        {`Et voici la première publicité de ${brand}.`}
      </h2>
      <p className={`text-base mb-5 max-w-[640px] ${muted}`}>
        Fabriquée à partir de votre site, prête pour Instagram et Facebook. Si vous la voulez, nous la mettons en ligne et nous la suivons pour vous. Vous ne payez que la diffusion, directement à la régie.
      </p>
      <div className="grid grid-cols-[3fr_2fr] md:grid-cols-[1fr_0.62fr] gap-3 md:gap-5 items-start max-w-[860px]">
        <img src={ads.feedUrl} alt={`Publicité carrée de ${brand}`} className="w-full rounded-2xl shadow-2xl" loading="lazy" />
        <img src={ads.storyUrl} alt={`Story publicitaire de ${brand}`} className="w-full rounded-2xl shadow-2xl" loading="lazy" />
      </div>
      <div className="mt-5">
        {launchHref && !launchRecorded && <a href={launchHref} className={buttonClass}>{label}</a>}
        {!launchHref && onLaunch && (
          <button type="button" onClick={onLaunch} disabled={launchRecorded} className={`${buttonClass} disabled:opacity-80`}>{label}</button>
        )}
        {launchRecorded && launchHref && <div className={`text-sm ${strong}`}>{label}</div>}
      </div>
    </section>
  )
}
